// @flow
import React, {useEffect} from 'react';
import {isEmpty, get} from 'lodash';
import {connect} from 'react-redux';
import {evaluateExperimentAndLog} from '@homeaway/abacus-client-utils';
import {isDated} from '../../utils/searchParamsUtils';
import {useFetchRecentlyViewedListings} from '../../hooks/useFetchRecentlyViewedListings/useFetchRecentlyViewedListings';
import {useFetchJudgeSerpData} from '../../hooks/useFetchjudgeSerpData/useFetchJudgeSerpData';
import {useFetchAmenityCarouselListings} from '../../hooks/useFetchAmenityCarouselListings/useFetchAmenityCarouselListings';
import transformReduxParamsToSearch from '../../queries/transformReduxParamsToSearchRequest';
import RecentlyViewedCarousel from '../RecentlyViewedCarousel/RecentlyViewedCarousel';
import {getDeviceType} from './utils/leadCarouselUtils';
import {triggerLeadCarouselMabPresentedEvent} from '../../utils/collectionEdapUtils';
import {
    SERP_AMENITIES_CAROUSEL,
    SERP_PREVIOUSLY_VIEWED_CAROUSEL,
    LEAD_CAROUSEL_PLACEMENT,
    EdapCarouselPlacementsMap
} from './utils/constants';
import type {
    ParsedSearchParams,
    PercentBookedResult
} from '../../queries/searchRequestQuery';

import AmenityCarousel from '../AmenityCarousel/AmenityCarousel';
import {buildSeeAllLink} from '../AmenityCarousel/utils/buildSeeAllLink';
import {VRBO_SERP_MULTIPLE_MODULES_WEB} from '../../abacus/experiments';

type OwnProps = {
    placement: string,
    resultCount: number,
    showMabCarousels: boolean,
    percentBooked: PercentBookedResult,
    results: any
};

type StateProps = {
    isDatedSearch: boolean,
    isMobile: boolean,
    isTablet: boolean,
    parsedParams: ParsedSearchParams,
    adultsCount: number,
    childrenCount: number,
    searchParams: any
};

type Props = OwnProps & StateProps;

const LeadCarouselContainer = ({
    isDatedSearch,
    isMobile,
    isTablet,
    parsedParams,
    placement,
    resultCount,
    showMabCarousels = true,
    results,
    searchParams,
    percentBooked,
    adultsCount,
    childrenCount
}: Props) => {
    const deviceType = getDeviceType(isMobile, isTablet);
    const carouselPosition = EdapCarouselPlacementsMap[placement];
    const {
        previouslyViewedListings,
        previouslyViewedListingsLoading
    } = useFetchRecentlyViewedListings(
        parsedParams,
        isMobile,
        showMabCarousels
    );
    const {
        amenityCarouselData,
        amenityCarouselDataLoading
    } = useFetchAmenityCarouselListings(
        searchParams,
        isMobile,
        parsedParams,
        results,
        showMabCarousels
    );
    const hasAmenityCarouselListings =
        showMabCarousels && !isEmpty(amenityCarouselData);
    const hasPrevViewedListings =
        showMabCarousels && !isEmpty(previouslyViewedListings);
    const carouselRequestsAreFinished =
        !previouslyViewedListingsLoading && !amenityCarouselDataLoading;

    let vrboSerpMultipleModulesWebTestValue = 0;
    if (hasAmenityCarouselListings || hasPrevViewedListings) {
        vrboSerpMultipleModulesWebTestValue = evaluateExperimentAndLog(
            VRBO_SERP_MULTIPLE_MODULES_WEB
        ).value;
    }

    const judgeSerpData = useFetchJudgeSerpData({
        resultCount: !vrboSerpMultipleModulesWebTestValue ? 0 : resultCount, // skip query if the test is in control
        isDatedSearch,
        deviceType,
        showRecentlyViewedCarousel: hasPrevViewedListings,
        showAmenityCarousel: hasAmenityCarouselListings,
        numFilters: searchParams.numFilters,
        percentBooked,
        adultsCount,
        childrenCount,
        carouselRequestsAreFinished
    });
    useEffect(() => {
        if (
            !isEmpty(judgeSerpData) &&
            placement === LEAD_CAROUSEL_PLACEMENT.TOP && // to call it only once per all placements
            judgeSerpData.result.every(
                ({level}) => level === LEAD_CAROUSEL_PLACEMENT.HIDE
            )
        ) {
            triggerLeadCarouselMabPresentedEvent();
        }
    }, [judgeSerpData, placement]);

    if (
        isEmpty(judgeSerpData) ||
        (!hasPrevViewedListings && !hasAmenityCarouselListings)
    ) {
        return null;
    }

    const chosenFilter = get(
        amenityCarouselData,
        `popularAmenitySearch.chosenFilter`,
        ''
    );
    const seeMoreUrl = hasAmenityCarouselListings
        ? buildSeeAllLink(searchParams, chosenFilter)
        : '';

    return judgeSerpData.result
        .filter(({level}) => level === placement)
        .map(({component}) => {
            if (
                component === SERP_PREVIOUSLY_VIEWED_CAROUSEL &&
                hasPrevViewedListings
            ) {
                return (
                    <section
                        className={`RecentlyViewedCarousel RecentlyViewedCarousel__${carouselPosition}`}
                    >
                        <RecentlyViewedCarousel
                            listings={previouslyViewedListings}
                            isMobile={isMobile}
                            position={carouselPosition}
                        />
                    </section>
                );
            }

            if (
                component === SERP_AMENITIES_CAROUSEL &&
                hasAmenityCarouselListings
            ) {
                return (
                    <AmenityCarousel
                        isMobile={isMobile}
                        seeMoreUrl={seeMoreUrl}
                        title={get(
                            amenityCarouselData,
                            `popularAmenitySearch.title`,
                            ''
                        )}
                        data={amenityCarouselData}
                        position={carouselPosition}
                    />
                );
            }

            return null;
        });
};

const mapStateToProps = (state) => {
    const {searchParams, system} = state;
    const {isMobile, isTablet} = system;
    const {params} = searchParams;
    const isDatedSearch = !!isDated(params);
    const parsedParams = transformReduxParamsToSearch(params);

    return {
        adultsCount: params.adultsCount,
        childrenCount: params.childrenCount,
        isDatedSearch,
        isMobile,
        isTablet,
        parsedParams,
        searchParams
    };
};

export default connect(mapStateToProps)(LeadCarouselContainer);
